import { browser } from '$app/environment';
import { PUBLIC_PADDLE_TOKEN } from '$env/static/public';
import {
    isCheckoutEnabled,
    isProd,
    cookie_consent_preferences
} from '$lib/stores/globals';
import * as Sentry from '@sentry/browser';
import {
    get_params_of_interest,
    logEvent,
    track_initiate_checkout,
    track_purchase
} from './logs';
import { get } from 'svelte/store';

let isInstalled = false;
let triedToInstall = false;
// The script failed to load (ad blocker, network). There's no retry, so later
// checkouts report the error instead of waiting forever.
let load_failed = false;
// Run once the script loads, e.g. a checkout requested while an earlier
// install (the root layout's idle-time one) is still loading.
/** @type {function[]} */
let on_loaded = [];

const blocked_path_patterns = [
    /^\/escape\/?$/,
    /^\/join\/?$/,
    /^\/folder\/?$/,
    /^\/terminal\/?$/,
    /^\/phone\/?$/,
    /^\/minutes\/?$/,
    /^\/classrooms?(?:\/|$)/,
    /^\/my-classrooms(?:\/|$)/,
    /^\/my-school(?:\/|$)/
];

export const is_paddle_blocked_path = (pathname = '') => {
    return blocked_path_patterns.some((pattern) => pattern.test(pathname));
};

// Unlike checkout errors, which Paddle shows inside its frame.
const dispatch_load_error = () =>
    document.dispatchEvent(new CustomEvent('paddle_load_error'));

/**
 *
 * @param {object} options
 * @param {function=} options.loaded
 * @param {string=} options.pathname
 */
export function install_paddle(options = {}) {
    const pathname =
        options.pathname || (browser ? window.location.pathname : '');

    if (is_paddle_blocked_path(pathname)) return;

    if (!isCheckoutEnabled) {
        console.log('checkout disabled');
        return;
    }

    if (options.loaded && isInstalled) options.loaded();
    else if (options.loaded && load_failed) dispatch_load_error();
    else if (options.loaded) on_loaded.push(options.loaded);

    if (!isInstalled && !triedToInstall && browser) {
        console.log(
            'install_paddle',
            options?.loaded ? 'with action' : 'without action'
        );

        triedToInstall = true;

        let d = document;
        let s = d.createElement('script');
        s.async = true;
        d.body.appendChild(s);

        s.onload = () => {
            isInstalled = true;
            console.log('Paddle loaded');
            if (!isProd) {
                console.log('Paddle sandbox mode');
                window.Paddle.Environment.set('sandbox');
            }

            window.Paddle.Initialize({
                token: PUBLIC_PADDLE_TOKEN,
                eventCallback(data) {
                    // console.log('eventCallback', data.name, data.data);

                    // Every checkout event carries the whole checkout, and the
                    // totals change on several (discount code entered, country
                    // changed, ...). BuyPage's order summary follows them.
                    if (data.data?.totals) {
                        document.dispatchEvent(
                            new CustomEvent('paddle_update', {
                                detail: data.data
                            })
                        );
                    }

                    switch (data.name) {
                        case 'checkout.loaded':
                            track_initiate_checkout();
                            break;

                        case 'checkout.closed':
                            console.log('checkout.closed');
                            document.dispatchEvent(
                                new CustomEvent('paddle_close', {
                                    detail: data.data
                                })
                            );
                            break;

                        case 'checkout.completed':
                            console.log('checkout.completed');
                            document.dispatchEvent(
                                new CustomEvent('paddle_success', {
                                    detail: data.data
                                })
                            );
                            track_purchase(
                                parseFloat(data.data.totals.total) -
                                    parseFloat(data.data.totals.tax)
                            );
                            break;
                        case 'checkout.customer.created':
                        case 'checkout.customer.updated':
                            logEvent('purchase:payment_step_2');
                            console.log('checkout.customer.created/updated');
                            if (get(cookie_consent_preferences)) {
                                if (data.data?.customer?.id)
                                    localStorage.setItem(
                                        'ctm_id',
                                        data.data.customer.id
                                    );
                                if (data.data?.customer?.address?.id)
                                    localStorage.setItem(
                                        'add_id',
                                        data.data.customer.address.id
                                    );
                                if (data.data?.customer?.email) {
                                    localStorage.setItem(
                                        'ctm_email',
                                        data.data.customer.email
                                    );
                                }
                            }
                            break;

                        case 'checkout.customer.removed':
                            console.log('checkout.customer.removed');
                            if (get(cookie_consent_preferences)) {
                                localStorage.removeItem('ctm_id');
                                localStorage.removeItem('add_id');
                            }
                            break;

                        case 'checkout.payment.failed':
                            console.log('checkout.payment.failed');
                            logEvent('purchase:payment_failed', {
                                payment_type:
                                    data?.data?.payment?.method_details?.type
                            });
                            break;

                        case 'checkout.error':
                            console.log('checkout.error', data?.data);
                            logEvent('purchase:checkout_error', {
                                error_code: data?.data?.error?.code,
                                error_type: data?.data?.error?.type,
                                error_message: data?.data?.error?.message
                            });
                            document.dispatchEvent(
                                new CustomEvent('paddle_error', {
                                    detail: data?.data
                                })
                            );
                            break;

                        case 'checkout.warning':
                            console.log('checkout.warning', data?.data);
                            logEvent('purchase:checkout_warning', {
                                warning_code: data?.data?.warning?.code,
                                warning_type: data?.data?.warning?.type,
                                warning_message: data?.data?.warning?.message
                            });
                            break;
                        default:
                        // console.log(data);
                    }
                }
            });

            on_loaded.splice(0).forEach((loaded) => loaded());
        };

        s.onerror = (e) => {
            console.error('Paddle Loading Error', e);
            Sentry.captureException('Paddle Loading Error');
            load_failed = true;
            on_loaded = [];
            dispatch_load_error();
        };
        s.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    }
}

/**
 * @param {object} options
 * @param {string} options.price_id
 * @param {string | null=} options.email
 * @param {string | null=} options.discount_id
 * @param {string=} options.feature
 * @param {string | null=} options.frame_target class of an element to embed
 *  the checkout in (Paddle's inline checkout) instead of the overlay
 */
export function paddle_purchase({
    price_id,
    email = null,
    discount_id = null,
    feature = 'generic',
    frame_target = null
}) {
    // console.log('paddle_purchase', price_id, email);
    if (!browser) return;

    if (is_paddle_blocked_path(window.location.pathname)) return;

    if (!isCheckoutEnabled) {
        console.log('checkout disabled');
        return;
    }
    if (!window.Paddle) {
        install_paddle({
            loaded: paddle_purchase.bind(null, {
                price_id,
                email,
                discount_id,
                feature,
                frame_target
            })
        });
        return;
    }

    // The visitor may have left the buy page while Paddle.js was loading.
    if (frame_target && !document.querySelector(`.${frame_target}`)) return;

    const theme =
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light';

    let ctm_id = null;
    let add_id = null;
    let ctm_email = null;

    if (get(cookie_consent_preferences)) {
        ctm_id = localStorage.getItem('ctm_id') || null;
        add_id = localStorage.getItem('add_id') || null;
        ctm_email = localStorage.getItem('ctm_email') || null;
    }

    let options = {
        settings: {
            theme,
            locale: 'en',
            ...(frame_target && {
                // BuyPage, the only inline checkout, is a standalone light page.
                theme: 'light',
                displayMode: 'inline',
                variant: 'one-page',
                frameTarget: frame_target,
                frameInitialHeight: '450',
                frameStyle:
                    'width: 100%; min-width: 312px; background-color: transparent; border: none;'
            })
        },
        items: [
            {
                priceId: price_id,
                quantity: 1
            }
        ],
        customData: {
            ...get_params_of_interest(),
            feature
        }
    };

    if (ctm_id && add_id) {
        options.customer = {
            id: ctm_id,
            address: {
                id: add_id
            }
        };
    } else if (email || ctm_email) {
        options.customer = {
            email: email || ctm_email
        };
    }

    if (discount_id) options.discountId = discount_id;

    window.Paddle.Checkout.open(options);
}
